/* eslint-disable no-console, react/function-component-definition, jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
import React, { useState, useEffect } from 'react';
import { Plus, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import logo from '../assets/logo.png';
import { MixRepository, MixDetails } from '../repositories/LocalDatabase';
import SmokeBackground from './SmokeBackground';
import MixDetailModal from './MixDetailModal';
import { mapDbMixToShishaMix } from '../repositories/mapper';
import { ShishaMix } from '../types';

const Home: React.FC = () => {
  const navigate = useNavigate();
  const [recipes, setRecipes] = useState<MixDetails[]>([]);
  const [selectedMix, setSelectedMix] = useState<ShishaMix | null>(null);
  const [isFirstVisit, setIsFirstVisit] = useState(true);

  useEffect(() => {
    const visited = localStorage.getItem('hf_has_visited');
    if (visited) {
      setIsFirstVisit(false);
    } else {
      localStorage.setItem('hf_has_visited', 'true');
      setIsFirstVisit(true);
    }
    const fetchRecipes = async () => {
      try {
        const data = await MixRepository.getAll();
        const userMixes = data.filter(m => m.user_id === 'local-user');
        const sorted = [...userMixes].sort(
          (a, b) => (b.rating || 0) - (a.rating || 0),
        );
        setRecipes(sorted.slice(0, 3));
      } catch (err) {
        console.error('Failed to load recipes', err);
      }
    };
    fetchRecipes();
  }, []);

  const handleCreateClick = () => {
    navigate('/create-recipe');
  };

  const handleSearchClick = () => {
    navigate('/recipes');
  };

  const containerStyle: React.CSSProperties = {
    padding: '40px 20px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '60px',
    animation: 'fadeIn 0.5s ease-out',
    position: 'relative',
    minHeight: '100%',
  };

  const heroSectionStyle: React.CSSProperties = {
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    padding: '80px 0',
    zIndex: 1, // Place hero content & logo in front of the smoke background (zIndex: -1)
  };

  const heroTextStyle: React.CSSProperties = {
    fontSize: 'var(--font-size-hero)',
    textAlign: 'center',
    zIndex: 2,
    margin: 0,
    textShadow: '0 2px 10px rgba(0,0,0,0.5)',
  };

  const heroJapaneseStyle: React.CSSProperties = {
    fontFamily: 'var(--font-heading-ja)',
  };

  const blurredLogoStyle: React.CSSProperties = {
    position: 'absolute',
    width: 'calc(var(--font-size-hero) * 5)',
    height: 'calc(var(--font-size-hero) * 2.5)',
    opacity: 0.2,
    filter: 'blur(1.5px)',
    zIndex: 1,
  };

  const actionButtonsContainerStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    alignItems: 'center',
    zIndex: 2,
    width: '100%',
    marginTop: isFirstVisit ? 0 : '60px',
  };


  const recipeSectionStyle: React.CSSProperties = {
    width: '100%',
    maxWidth: '800px',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    position: 'relative',
    zIndex: 1, // Place recommended recipe cards in front of the smoke background (zIndex: -1)
  };

  const gridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
    gap: '20px',
  };

  const cardStyle: React.CSSProperties = {
    backgroundColor: 'var(--color-surface)',
    borderRadius: '12px',
    padding: '20px',
    border: '1px solid rgba(231, 228, 223, 0.1)',
    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    cursor: 'pointer',
    transition: 'transform 0.2s',
  };

  return (
    <div style={containerStyle}>
      <SmokeBackground />
      {/* Hero Section */}
      {isFirstVisit && (
        <div style={heroSectionStyle}>
          <img src={logo} alt="Blurred Logo" style={blurredLogoStyle} />
          <h1 style={heroTextStyle}>
            <span style={heroJapaneseStyle}>
              <span className="hero-text-part">新たな出会いを<span className="pc-only-inline">、</span></span>
              <span className="hero-text-part">最高のミックスと<span className="pc-only-inline">。</span></span>
            </span>
          </h1>
        </div>
      )}

      {/* Action Buttons */}
      <div style={actionButtonsContainerStyle}>
        <button
          type="button"
          className="hero-action-btn"
          onClick={handleCreateClick}
        >
          <Plus className="hero-action-icon" size={36} />
          レシピを登録する
        </button>
        <button
          type="button"
          className="hero-action-btn"
          onClick={handleSearchClick}
        >
          <Search className="hero-action-icon" size={36} />
          レシピを検索する
        </button>
      </div>

      {/* Recommended Recipes */}
      <div style={recipeSectionStyle}>
        <h3
          style={{
            fontSize: 'var(--font-size-h3)',
            borderBottom: '1px solid rgba(231, 228, 223, 0.1)',
            paddingBottom: '10px',
          }}
        >
          おすすめのレシピ
        </h3>
        <div style={gridStyle}>
          {recipes.map((recipe) => {
            const mixString = recipe.flavors
              .map((f) => `${f.flavor_name} ${f.ratio}%`)
              .join(' / ');
            return (
              <div
                key={recipe.id}
                className="ripple-card"
                style={cardStyle}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.borderColor = 'var(--color-primary)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'none';
                  e.currentTarget.style.borderColor =
                    'rgba(231, 228, 223, 0.1)';
                }}
                onClick={() => {
                  setSelectedMix(mapDbMixToShishaMix(recipe));
                }}
              >
                <h4
                  style={{
                    margin: 0,
                    fontSize: 'var(--font-size-body)',
                    color: 'var(--color-accent)',
                  }}
                >
                  {recipe.name}
                </h4>
                <p
                  style={{
                    margin: 0,
                    fontSize: 'var(--font-size-caption)',
                    opacity: 0.8,
                  }}
                >
                  {mixString}
                </p>
              </div>
            );
          })}
        </div>
      </div>

      {selectedMix && (
        <MixDetailModal
          mix={selectedMix}
          onClose={() => setSelectedMix(null)}
          onLike={() => {}}
          onBookmark={() => {}}
          onAddComment={() => {}}
          isLiked={false}
          isBookmarked={false}
        />
      )}
    </div>
  );
};

export default Home;
